import { store } from 'store/index'
import { messagesActions, messagesSelectors } from 'store/messages'
import _ from 'lodash'
import { captchaAxios, masterApiService } from 'api/config'
import axios from 'axios'
import { User } from 'types/user'
import { Ticket } from 'types/ticket'
import { settingsActions, settingsSelectors } from 'store/settings'
import { assignGuestChatsToCurrentUser, getMyChats, markChatAsRead } from 'api/chats'
import { ChatResponseModeEnum } from 'types/chat'
import { TicketAdditionalField } from 'types/ticket-additional-field'
import { useQuery } from '@tanstack/react-query'

export const TICKETS_QUERY_KEY = 'tickets'

export type TicketParams = {
  title: string
  description: string
  additionalTicketInfo?: Record<string, any>
}

export type GuestTicketParams = TicketParams & {
  email: string
}

type GuestTicketResponse = {
  ticker: Ticket
  user: User
  token: string
  refreshToken: string | null
}

export const openNewTicketForGuest = async ({ title, description, email, additionalTicketInfo }: GuestTicketParams) => {
  const state = store.getState()
  const chatId = messagesSelectors.chatId(state)
  const guestChats = messagesSelectors.guestChats(state)
  const guestChatIds = _.map(guestChats, '_id')

  if (!chatId) {
    return
  }

  try {
    const { data } = await captchaAxios.post<GuestTicketResponse>(`tickets/chats/${chatId}/guest/${email}`, {
      title,
      description,
      otherChats: [...guestChatIds.filter((id) => id !== chatId)],
      chatId,
      additionalTicketInfo,
    })
    console.debug({
      openedTicket: data,
    })
    store.dispatch(settingsActions.setToken(data.token))
    store.dispatch(settingsActions.setUserDetails(data.user))
    store.dispatch(
      messagesActions.updateCurrentChat({
        responseMode: ChatResponseModeEnum.OPERATOR,
      }),
    )
    console.debug({ openNewTicketForGuest: data })
    await getMyChats({})
    await markChatAsRead(chatId)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const openNewTicketForLoggedUser = async ({ title, description, additionalTicketInfo }: TicketParams) => {
  const state = store.getState()
  const chatId = messagesSelectors.chatId(state)

  if (!chatId) {
    return
  }

  try {
    const { data } = await axios.post(`tickets/chats/${chatId}/user`, {
      title,
      description,
      chatId,
      additionalTicketInfo,
    })
    console.debug({ openNewTicketForLoggedUser: data })
    await assignGuestChatsToCurrentUser()
    store.dispatch(
      messagesActions.updateCurrentChat({
        responseMode: ChatResponseModeEnum.OPERATOR,
      }),
    )
    await getMyChats({})
    await markChatAsRead(chatId)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const openTicket = (params: GuestTicketParams) => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  if (isLogged) {
    return openNewTicketForLoggedUser(params)
  } else {
    return openNewTicketForGuest(params)
  }
}

export const closeTicket = async (ticketId: string, closingReason?: string) => {
  if (!ticketId) {
    return
  }
  try {
    const { data } = await axios.put<Ticket>(`tickets/${ticketId}/close`, {
      closingReason,
    })
    store.dispatch(messagesActions.updateCurrentChat({ ticket: data }))
    console.debug({ closeTicket: data })
  } catch (e) {
    return Promise.reject(e)
  }
}

export const fetchTicketAdditionalFields = () =>
  masterApiService.getList<TicketAdditionalField>('ticket-additional-fields')

export const useGetTicketAdditionalFields = () => {
  const { data, ...result } = useQuery({
    queryKey: [TICKETS_QUERY_KEY],
    queryFn: fetchTicketAdditionalFields,
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  return {
    ...result,
    data: data || [],
  }
}
